import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import QuizOrSurveyModal from "../../../../../components/CourseBuilder/QuizOrSurveyModal";
import LoadingSpinnerWithBackdrop from "../../../../../components/LoadingSpinner/LoadingSpinnerWithBackdrop";
import QuestionBankModal from "../../../../../components/QuestionBank/QuestionBankModal";
import AddRepoItemVersionConfirmationModal from "../../../../../components/WarningModal/AddRepoItemVersionConfirmationModal";
import { quizQuestionTypes } from "../../../../../constants";
import EditQuestion from "../../../Courses/CourseBuilder/EditQuestion";
import { useRepositoryQuiz } from "../../../../../hooks/repositoryItems";

const AddNewQuizVersion = ({
  courseNames,
  onCancel,
  onClose,
  open,
  loading,
  newQuiz
}) => {
  const [questionBankOpen, setQuestionBankOpen] = useState(false);
  const [editQuestionModalOpen, setEditQuestionModalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [quizSettingsModalOpen, setQuizSettingsModalOpen] = useState(true);
  const [newQuestionType, setNewQuestionType] = useState(
    quizQuestionTypes.trueFalse
  );
  const [questionData, setQuestionData] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const isSurvey = newQuiz && newQuiz.quizType === "survey";
  const quizTypeName = isSurvey ? "survey" : "quiz";

  const {
    quiz,
    quizErrors,
    fetchQuiz,
    updateQuiz,
    bulkUpdateQuestions,
    updateQuizQuestionsRelationships,
    updateQuestion,
    createQuestion,
    importQuizQuestions
  } = useRepositoryQuiz(newQuiz);

  useEffect(() => setQuizSettingsModalOpen(open), [open]);

  const handleUpdateQuiz = newQuizData => {
    return updateQuiz({ id: quiz.id, ...newQuizData }).then(() => {
      if (!!quizErrors) {
        setQuestionBankOpen(false);
      } else {
        setQuestionBankOpen(true);
      }
    });
  };

  const handleSaveQuestionsOrShowConfirmation = newQuestionsData => {
    setQuestionData(newQuestionsData);
    if (!!courseNames && courseNames.length > 0) {
      setConfirmationModalOpen(true);
    } else {
      handleSaveQuestions(newQuestionsData);
    }
  };

  const handleSaveQuestions = newQuestionsData => {
    const data = questionData ? questionData : newQuestionsData;
    bulkUpdateQuestions(data);
    updateQuizQuestionsRelationships({ ...quiz, questions: data });
    setIsSaved(true);
    setConfirmationModalOpen(false);
    setQuizSettingsModalOpen(false);
    setQuestionBankOpen(false);
    setQuestionData(null);
    onClose();
  };

  const closeConfirmation = () => {
    setConfirmationModalOpen(false);
  };

  const handleAddNewQuestion = questionType => {
    setNewQuestionType(questionType);
    setEditQuestionModalOpen(true);
  };

  const handleEditQuestion = question => {
    setNewQuestionType(question.questionType);
    setSelectedQuestion(question);
    setEditQuestionModalOpen(true);
  };

  const handleSaveQuestion = (
    newQuestionData,
    newQuestionTypeToCreate,
    questionId,
    resetForm
  ) => {
    if (questionId) {
      updateQuestion(questionId, newQuestionData).then(() => {
        setUpdates(newQuestionTypeToCreate, resetForm);
      });
    } else {
      createQuestion(newQuestionData).then(() => {
        setUpdates(newQuestionTypeToCreate, resetForm);
      });
    }
  };

  const handleUploadQuestions = file => {
    return importQuizQuestions(file).then(response => {
      if (response.errors) {
        fetchQuiz(quiz.id);
      }
      return response;
    });
  };

  const setUpdates = (newQuestionTypeToCreate, resetForm) => {
    if (!newQuestionTypeToCreate) {
      setEditQuestionModalOpen(false);
    } else {
      resetForm();
      setNewQuestionType(newQuestionTypeToCreate);
    }
    setSelectedQuestion(null);
  };

  const handleQuizOrSurveyClose = () => {
    setConfirmationModalOpen(false);
    if (isSaved) {
      onClose();
    } else {
      onCancel();
    }
  };

  return (
    <>
      {loading && <LoadingSpinnerWithBackdrop />}
      <AddRepoItemVersionConfirmationModal
        courseNames={courseNames}
        open={confirmationModalOpen}
        onSubmit={handleSaveQuestions}
        onClose={closeConfirmation}
      />
      <QuizOrSurveyModal
        onClose={handleQuizOrSurveyClose}
        open={quizSettingsModalOpen}
        onSubmit={handleUpdateQuiz}
        type={quizTypeName}
        isNewVersion={true}
        quizOrSurveyData={quiz}
        loading={loading}
        errors={quizErrors}
      />
      <QuestionBankModal
        open={questionBankOpen}
        onClose={() => {
          setQuestionBankOpen(false);
          setSelectedQuestion(null);
        }}
        title={quiz ? quiz.name : ""}
        questions={quiz ? quiz.questions : []}
        quizId={quiz && !isSurvey ? quiz.id.toString() : ""}
        surveyId={quiz && isSurvey ? quiz.id.toString() : ""}
        type={quizTypeName}
        onSubmit={handleSaveQuestionsOrShowConfirmation}
        onAddNewQuestion={handleAddNewQuestion}
        onEditQuestion={handleEditQuestion}
        onUploadQuestions={handleUploadQuestions}
      />
      <EditQuestion
        open={editQuestionModalOpen}
        onClose={() => {
          setEditQuestionModalOpen(false);
          setSelectedQuestion(null);
        }}
        newQuestionType={newQuestionType}
        quizId={quiz ? quiz.id.toString() : ""}
        isSurveyQuestion={isSurvey}
        onSaveQuestion={handleSaveQuestion}
        existingQuestionData={selectedQuestion}
      />
    </>
  );
};

AddNewQuizVersion.propTypes = {
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called to close the Modal
   */
  onClose: PropTypes.func,
  /**
   * A list of associated course names
   */
  courseNames: PropTypes.arrayOf(PropTypes.string),
  /**
   * True if data is loading
   */
  loading: PropTypes.bool,
  /**
   * The item version that was just created
   */
  newItem: PropTypes.object,
  /**
   * The quiz that was just created
   */
  newQuiz: PropTypes.object,
  /**
   * Called to cleanup eagerly created data
   */
  onCancel: PropTypes.func
};

export default AddNewQuizVersion;
